import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { SessionService } from '../services/session.service';

/** Auth routes that must not trigger a silent refresh or forced sign-out on 401. */
const AUTH_NO_REFRESH =
  /\/identity\/auth\/(login|register|refresh|forgot-password|reset-password)(\/|$|\?)/;

/** Attaches the bearer token; on 401, rotates via refresh token and retries once. */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const session = inject(SessionService);
  const auth = inject(AuthService);
  const token = session.token();

  const request = token
    ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
    : req;

  return next(request).pipe(
    catchError((error: unknown) => {
      if (!(error instanceof HttpErrorResponse) || error.status !== 401) {
        return throwError(() => error);
      }

      if (AUTH_NO_REFRESH.test(req.url) || req.headers.has('X-Auth-Retry')) {
        return throwError(() => error);
      }

      if (!session.refreshToken()) {
        auth.logout();
        return throwError(() => error);
      }

      return auth.refreshSession().pipe(
        switchMap((refreshed) =>
          next(
            req.clone({
              setHeaders: {
                Authorization: `Bearer ${refreshed.token}`,
                'X-Auth-Retry': '1',
              },
            }),
          ),
        ),
        catchError((refreshError) => {
          auth.logout();
          return throwError(() => refreshError);
        }),
      );
    }),
  );
};
